import { useEffect, useRef, useState } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

/** Inclusive date range as YYYY-MM-DD strings, matching what the report forms send. */
export interface DateRange {
  from: string;
  to: string;
}

type QuickRangeKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth';

interface QuickDateFiltersProps {
  onChange: (range: DateRange) => void;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const toRange = (from: Dayjs, to: Dayjs): DateRange => ({
  from: from.format(DATE_FORMAT),
  to: to.format(DATE_FORMAT),
});

const presetRange = (key: QuickRangeKey): DateRange => {
  const today = dayjs().startOf('day');
  // Reports run on completed days: most presets end yesterday (today's data is still in flight).
  // Only Today and This Month intentionally include today.
  const yesterday = today.subtract(1, 'day');
  switch (key) {
    case 'today':
      return toRange(today, today);
    case 'yesterday':
      return toRange(yesterday, yesterday);
    case 'last7':
      return toRange(yesterday.subtract(6, 'day'), yesterday);
    case 'last30':
      return toRange(yesterday.subtract(29, 'day'), yesterday);
    case 'thisMonth':
      return toRange(today.startOf('month'), today);
    case 'lastMonth': {
      const lastMonth = today.subtract(1, 'month');
      return toRange(lastMonth.startOf('month'), lastMonth.endOf('month'));
    }
  }
};

interface QuickDateFiltersProps {
  onChange: (range: DateRange) => void;
  /**
   * Bump this (e.g. a counter) whenever the user edits the form's own date pickers manually, so the
   * active chip flips to "Custom". The component renders only the chips — the host form keeps its
   * own from/to date inputs, so there is no duplicate set of date fields.
   */
  manualEditSignal?: number;
}

/**
 * Quick date presets (Today / Yesterday / Last 7 / Last 30 / This Month / Last Month) rendered as
 * clickable chips (primary colour when active), plus Custom. The host form owns the from/to date
 * inputs; presets overwrite them via onChange, and manual edits flip the active chip to Custom via
 * manualEditSignal. Selecting Custom emits nothing — it just marks that the dates are hand-set.
 */
const QuickDateFilters = ({ onChange, manualEditSignal = 0 }: QuickDateFiltersProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [selection, setSelection] = useState<QuickRangeKey | null>('last7');
  const isFirstRender = useRef(true);

  // Apply the default preset (Last 7 Days) immediately so the dates are set without needing to
  // re-pick the already-selected option.
  useEffect(() => {
    onChange(presetRange('last7'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A manual edit of the host's date pickers clears the active preset (dates no longer match one).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (manualEditSignal > 0) {
      setSelection(null);
    }
  }, [manualEditSignal]);

  const handlePreset = (key: QuickRangeKey) => {
    setSelection(key);
    onChange(presetRange(key));
  };

  const presets: QuickRangeKey[] = ['today', 'yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth'];

  return (
    <Box>
      <Typography variant="textMD"
        sx={{ fontWeight: 600, mb: '6px', display: 'block' }}>
        {t('reportingV2.date-range')}
      </Typography>
      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
        role="group"
        aria-label={t('reportingV2.date-range')}>
        {presets.map((key) => {
          const active = selection === key;
          return (
            <Chip
              key={key}
              label={t(`reportingV2.date-presets.${key}`)}
              clickable
              onClick={() => handlePreset(key)}
              variant={active ? 'filled' : 'outlined'}
              aria-pressed={active}
              sx={{
                fontWeight: active ? 600 : 400,
                bgcolor: active ? theme.palette.primary.main : 'transparent',
                color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
                borderColor: active ? theme.palette.primary.main : theme.palette.custom.gray[300],
                '&:hover': {
                  bgcolor: active ? theme.palette.primary.dark : theme.palette.custom.gray[100],
                },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default QuickDateFilters;
